import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, MessageCircle, ClipboardList, ChevronDown, Zap } from "lucide-react";
import { phoneTel, waLink, DEFAULT_WA_MSG } from "../../lib/constants";
import { useSettings } from "../../lib/SettingsContext";
import { EASE } from "../Reveal";

const Masked = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = ({ onRequest }) => {
  const s = useSettings();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const fade = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden carbon-bg noise-overlay pt-24 pb-20"
    >
      <div className="absolute inset-y-0 left-0 w-16 sm:w-24 tread-pattern opacity-70 pointer-events-none" />
      <div className="absolute -top-32 right-1/4 w-[540px] h-[540px] rounded-full bg-yellow-400/10 blur-[140px] pointer-events-none" />

      <motion.div
        style={{ opacity: fade }}
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-center relative z-10 w-full"
      >
        <div>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.6, ease: EASE }}
            className="inline-flex items-center gap-2 border border-yellow-400/40 text-yellow-400 text-xs font-semibold px-3 py-1.5 rounded-full mb-7"
          >
            <span className="w-2 h-2 rounded-full bg-yellow-400 animate-pulse" />
            {s.hero_badge}
          </motion.div>

          <h1
            data-testid="hero-title"
            className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.18] text-white tracking-tight"
          >
            <Masked delay={0.2}>{s.hero_line1}</Masked>
            <Masked delay={0.38}>
              <span className="text-yellow-400">{s.hero_line2}</span>
            </Masked>
            <Masked delay={0.56}>
              <span className="text-zinc-500 text-xl sm:text-2xl lg:text-3xl font-bold">{s.hero_line3}</span>
            </Masked>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.7, ease: EASE }}
            className="mt-6 text-base sm:text-lg text-zinc-400 leading-relaxed max-w-xl"
          >
            {s.hero_desc}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.7, ease: EASE }}
            className="mt-8 flex flex-wrap items-center gap-3"
          >
            <a
              data-testid="hero-call-btn"
              href={phoneTel(s.phone_display)}
              className="flex items-center gap-2 bg-yellow-400 text-black font-bold px-6 py-3.5 rounded-full hover:bg-yellow-300 hover:scale-[1.03] active:scale-[0.98] transition-all duration-200 shadow-[0_0_36px_rgba(255,199,0,0.3)]"
            >
              <Phone className="w-5 h-5" />
              اتصل الآن — <span dir="ltr">{s.phone_display}</span>
            </a>
            <a
              data-testid="hero-whatsapp-btn"
              href={waLink(DEFAULT_WA_MSG, s.whatsapp)}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 border border-zinc-600 text-white font-semibold px-6 py-3.5 rounded-full hover:border-green-400 hover:text-green-400 transition-colors duration-200"
            >
              <MessageCircle className="w-5 h-5" />
              واتساب
            </a>
            <button
              data-testid="hero-request-btn"
              onClick={() => onRequest()}
              className="flex items-center gap-2 text-yellow-400 font-semibold px-4 py-3.5 hover:gap-3 transition-all duration-200"
            >
              <ClipboardList className="w-5 h-5" />
              اطلب الخدمة
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.05, duration: 0.8 }}
            className="mt-10 flex items-center gap-8 text-sm text-zinc-500"
          >
            <div><span className="font-display text-2xl font-bold text-white block">24/7</span>خدمة طوارئ</div>
            <div className="w-px h-10 bg-zinc-800" />
            <div><span className="font-display text-2xl font-bold text-white block">+10</span>خدمات متنقلة</div>
            <div className="w-px h-10 bg-zinc-800" />
            <div><span className="font-display text-2xl font-bold text-white block">كل الرياض</span>تغطية شاملة</div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.4, ease: EASE }}
          className="relative hidden lg:block"
        >
          <div className="absolute -inset-10 rounded-full border border-dashed border-yellow-400/20 animate-spin-slow pointer-events-none" />
          <motion.div style={{ y: imgY }} className="relative">
            <div className="absolute -inset-3 border border-yellow-400/40 clip-frame pointer-events-none" />
            <div className="absolute -top-16 -right-16 w-72 h-72 bg-yellow-400/20 blur-[110px] rounded-full pointer-events-none" />
            <img
              data-testid="hero-image"
              src={s.hero_image}
              alt="فان بنشر متنقل نجم الإطارات في الرياض"
              className="clip-frame w-full h-[440px] lg:h-[520px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent clip-frame pointer-events-none" />
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1, duration: 0.7, ease: EASE }}
              className="absolute bottom-8 right-6 bg-black/80 backdrop-blur-md border border-yellow-400/30 rounded-xl px-4 py-3 flex items-center gap-3"
            >
              <span className="w-10 h-10 rounded-lg bg-yellow-400 flex items-center justify-center">
                <Zap className="w-5 h-5 text-black" />
              </span>
              <div>
                <div className="text-white text-sm font-bold">وصول سريع</div>
                <div className="text-zinc-400 text-xs">الفني ينطلق إليك فورًا</div>
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 text-zinc-600"
      >
        <ChevronDown className="w-5 h-5 animate-bounce" />
      </motion.div>
    </section>
  );
};
